import BreadCrumbs from 'components/breadcrumbs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeft,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  RefreshCw,
  Search,
} from 'react-feather'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { Card, CardBody, Col, Collapse, Row } from 'reactstrap'
import colors from 'Theme/colors'
import { formatDatePMBV2 } from 'utils/formatDate'
import LoadingAnimation from 'components/LoadingAnimation'
import Skeleton from 'components/Skeleton'
import { useSelector } from 'react-redux'
import { URL_MUTATION_REPORT } from 'utils/pathUrl'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import {
  useFetchMutationInventoriesQuery,
  useFetchMutationReportQuery,
  useFetchMutationSummaryQuery,
} from '../mutationReportSlice'
import {
  useLazyFetchMapSchemasQuery,
  useLazyGetSchemaDetailQuery,
} from '../../map-schema/mapSchemaApiSlice'
import CustomButton from 'components/Buttons/CustomButton'
import { TRACE_CACHE_TTL } from '../traceCache'
import { handleDownloadExcel } from '../downloadMutationReport'
import {
  STATUS_ORDER,
  addToBreakdown,
  SYSTEM_METHODS,
  buildProducts,
  conversionBalance,
  destinationUsage,
  recipientUsage,
  exportSources,
  processComparison,
  schemaKey,
  stepHasSchema,
  stepStandard,
  summaryToMap,
} from '../traceModel'
import PMBProgressBarExportFile from 'components/PMBProgressBarExportFile'
import { capitalizeEveryWord, useDebounce } from 'helpers'
import { getWorkingHoursBgColor, getWorkingHoursTextColor } from 'utility/Utils'

const TYPE_LABEL_MAP = {
  TRANSFER_IN: 'Transaksi',
  INVENTORY_GROUP: 'Packing',
  PROCESS: 'Process',
  INVENTORY_WAREHOUSE: 'Gudang',
}

// Destination status (FORWARD), in display order
const STATUS_META = {
  'Sudah Keluar': { label: 'Sudah Dikirim', color: colors.primary[500] },
  'Hapus - Jual': { label: 'Terjual', color: colors.primary[200] },
  Diproses: { label: 'Diproses jadi barang lain', color: colors.warning[500] },
  'Dalam Stok': { label: 'Masih di Gudang', color: colors.success[600] },
  'Hapus - Buang': { label: 'Dibuang', color: colors.neutral[400] },
}

// Same colour rule as the ESG dashboard (utility/Utils, "Jam Kerja"):
// material used as % of the schema standard. <= 100% green,
// > 100% to 120% yellow, > 120% red, grey when it cannot be compared.
const NA_TONE = { bg: '#f3f4f6', fg: '#6b7280' }

// Information chips under a material (Theme/colors)
const schemaTone = (pct) =>
  pct == null
    ? NA_TONE
    : { bg: getWorkingHoursBgColor(pct), fg: getWorkingHoursTextColor(pct) }

const SCHEMA_LEGEND = [
  { pct: 100, text: 'Sampai dengan 100% dari standar skema' },
  { pct: 110, text: 'Lebih dari 100% sampai 120%' },
  { pct: 130, text: 'Lebih dari 120%' },
  { pct: null, text: 'Skema tidak ditemukan atau satuan berbeda' },
]
// Source Lot IDs listed per material before "+N lainnya"
const LOT_PREVIEW_COUNT = 3
// Table and detail sit side by side from Bootstrap's lg breakpoint
const DESKTOP_QUERY = '(min-width: 992px)'

const fmtQty = (n, unit) =>
  `${newFormatThousandSeparator(Number(n) || 0)} ${capitalizeEveryWord(unit || '')}`.trim()

// Trace data is cached (memory + IndexedDB, see mutationReportSlice), so a
// response already loaded is shown as is instead of being fetched again
// (the app default refetches on every mount and argument change)
const CACHED = { refetchOnMountOrArgChange: false }

// The whole lot is loaded in one request: the table, the lot numbers and
// the Excel export all read the same response (search runs on the client)
const LOT_PAGE_SIZE = 999

// Server-side sort options of laporevents/inventories
const SORT_OPTIONS = [
  { key: 'newest', label: 'Terbaru', sortby: 'createdAt', sortdir: 'desc' },
  { key: 'oldest', label: 'Terlama', sortby: 'createdAt', sortdir: 'asc' },
  { key: 'nameAsc', label: 'Nama A-Z', sortby: 'name', sortdir: 'asc' },
  { key: 'nameDesc', label: 'Nama Z-A', sortby: 'name', sortdir: 'desc' },
]
// The list opens sorted by name, in both directions
const DEFAULT_SORT_KEY = 'nameAsc'

// Item name A-Z ("Item 2" before "Item 10"), then lot / code
const byName = (a, b) =>
  String(a?.item?.nameItem ?? '').localeCompare(
    String(b?.item?.nameItem ?? ''),
    'id',
    { numeric: true, sensitivity: 'base' },
  ) ||
  String(a?.lotCode ?? a?.ownerCode ?? '').localeCompare(
    String(b?.lotCode ?? b?.ownerCode ?? ''),
    'id',
    { numeric: true },
  )

const cardStyle = {
  backgroundColor: colors.white,
  border: `1px solid ${colors.neutral[200]}`,
  borderRadius: 14,
}

export default function DetailMutationReport() {
  const { id } = useParams()

  const { ownerData } = useSelector((e) => e.dashboard)
  const location = useLocation()
  const rowFromState = location.state?.rowData
  const [searchParams, setSearchParams] = useSearchParams()

  const direction = searchParams.get('direction')
  const dir = direction?.toUpperCase() === 'BACKWARD' ? 'BACKWARD' : 'FORWARD'
  const isBackward = dir === 'BACKWARD'
  const backwardParam = isBackward ? true : undefined
  // The lot's inventories are finished goods only when tracing their source;
  // going forward they are just goods (they may still be processed)
  const itemLabel = isBackward ? 'barang jadi' : 'barang'
  const ItemLabel = capitalizeEveryWord(itemLabel)

  const handleChangeDirection = (nextDir) => {
    if (nextDir === dir) return
    setSearchParams(
      { direction: nextDir },
      { replace: true, state: location.state },
    )
  }

  const { data: fetchedReportData, isLoading: isLoadingReport } =
    useFetchMutationReportQuery({ primaryLotId: id }, { skip: !!rowFromState })

  const row = rowFromState ?? fetchedReportData?.items?.[0] ?? null
  const escrowpaymentId = row?.escrowpaymentId
  // Wait for the report row (escrowpaymentId); firing before it is known
  // would send every request twice. A missing row does not block the page.
  const canFetch = !!id && (!!row || !isLoadingReport)

  const [selectedId, setSelectedId] = useState(null)
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia(DESKTOP_QUERY).matches,
  )
  const detailRef = useRef(null)
  const rowRefs = useRef({})
  const scrollToDetailOnSelect = useRef(false)

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_QUERY)
    const onChange = (e) => setIsDesktop(e.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  // "Kembali ke atas" once the page is scrolled past the first screen.
  // The app layout scrolls its content container (App.js), not the window,
  // so listen to the nearest scrolling ancestor of this page.
  const pageRef = useRef(null)
  const isPageLoading = !rowFromState && isLoadingReport
  const scrollerRef = useRef(null)
  const [showBackToTop, setShowBackToTop] = useState(false)
  useEffect(() => {
    let el = pageRef.current?.parentElement
    while (el && !/(auto|scroll)/.test(getComputedStyle(el).overflowY)) {
      el = el.parentElement
    }
    const scroller = el || window
    scrollerRef.current = scroller
    const top = () => (el ? el.scrollTop : window.scrollY)
    const onScroll = () => setShowBackToTop(top() > 400)
    onScroll()
    scroller.addEventListener('scroll', onScroll, { passive: true })
    return () => scroller.removeEventListener('scroll', onScroll)
    // The page root only exists after the full-page loader is gone
  }, [isPageLoading])

  const scrollToDetail = () =>
    detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  // Desktop: the detail is sticky, so only scroll when it is out of view
  // (e.g. sticky is not applied) and start the new detail at its top
  const revealDetail = () => {
    const el = detailRef.current
    if (!el) return
    if (!isDesktop) {
      scrollToDetail()
      return
    }
    el.scrollTop = 0
    const { top } = el.getBoundingClientRect()
    if (top < 0 || top > window.innerHeight / 2) scrollToDetail()
  }

  // Mobile: the detail sits below the table; desktop: it may be off-screen
  // after picking a row far down a long page
  useEffect(() => {
    if (!scrollToDetailOnSelect.current) return
    scrollToDetailOnSelect.current = false
    revealDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId])

  const handleSelectRow = (rowId) => {
    if (rowId === selectedId) revealDetail()
    else scrollToDetailOnSelect.current = true
    setSelectedId(rowId)
  }

  // Mobile: back to the row that was opened
  const backToList = () =>
    rowRefs.current[selectedId]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState(DEFAULT_SORT_KEY)
  const sort = SORT_OPTIONS.find((o) => o.key === sortKey) || SORT_OPTIONS[0]

  const applySearch = useDebounce((value) => {
    setSearch(value.trim())
  })

  // "Alur proses" status filter ('' = every status)
  const [flowStatus, setFlowStatus] = useState('')
  // One toggle for every KPI card, so their details open and close together
  const [kpiDetailsOpen, setKpiDetailsOpen] = useState(false)
  const [isDownload, setIsDownload] = useState(false)
  const [firstRows, setFirstRows] = useState(0)
  const [totalRows, setTotalRows] = useState(0)
  // Set by the refresh button: every trace must be newer than this moment
  const [refreshAt, setRefreshAt] = useState()

  // Switching direction starts a fresh trace
  useEffect(() => {
    setSelectedId(null)
    setFlowStatus('')
  }, [dir])

  const {
    currentData: summaryData,
    isLoading: isLoadingSummary,
    isFetching: isFetchingSummary,
    isError: isSummaryError,
  } = useFetchMutationSummaryQuery(
    {
      filterSummary: 'ALL',
      primaryLotId: id,
      backward: backwardParam,
      refreshAt,
    },
    { skip: !canFetch, ...CACHED },
  )
  // `isLoading` is only true on the very first load; a direction that is not
  // cached yet has no `currentData` while it loads
  const isSummaryLoading =
    canFetch && !isSummaryError && summaryData === undefined

  const {
    currentData: inventoriesData,
    isLoading: isLoadingInventories,
    isFetching: isFetchingInventories,
    isError: isInventoriesError,
  } = useFetchMutationInventoriesQuery(
    {
      escrowpaymentId,
      page: 0,
      size: LOT_PAGE_SIZE,
      primaryLotId: id,
      backward: backwardParam,
      sortby: sort.sortby,
      sortdir: sort.sortdir,
      refreshAt,
    },
    { skip: !canFetch, ...CACHED },
  )
  // `currentData` belongs to the current direction and sort only, so the
  // cards never show the numbers of the other direction while loading
  const isLotLoading =
    canFetch && !isInventoriesError && inventoriesData === undefined

  // FORWARD: current stock of the inventories this lot was split from.
  // Read from the BACKWARD trace, but never older than the FORWARD cache,
  // because stock keeps changing.
  const { currentData: sourceSummary, isFetching: isFetchingSourceSummary } =
    useFetchMutationSummaryQuery(
      {
        filterSummary: 'ALL',
        primaryLotId: id,
        backward: true,
        maxCacheAge: TRACE_CACHE_TTL.forward,
        refreshAt,
      },
      { skip: !canFetch || isBackward, ...CACHED },
    )

  const sourceById = useMemo(() => summaryToMap(sourceSummary), [sourceSummary])

  const isBusy =
    isLoadingInventories ||
    isLoadingSummary ||
    isFetchingInventories ||
    isFetchingSummary

  // Name sorts are also applied here, so the order never depends on how the
  // API sorts `name`
  const allInventories = useMemo(() => {
    const items = inventoriesData?.items || []
    if (sort.sortby !== 'name') return items
    const sorted = [...items].sort(byName)
    return sort.sortdir === 'desc' ? sorted.reverse() : sorted
  }, [inventoriesData, sort.sortby, sort.sortdir])

  // Search on what the table shows: item name, inventory code and lot
  const inventories = useMemo(() => {
    const q = search.toLowerCase()
    if (!q) return allInventories
    return allInventories.filter((inv) =>
      [inv?.item?.nameItem, inv?.ownerCode, inv?.lotCode, inv?.lotId].some(
        (v) =>
          String(v ?? '')
            .toLowerCase()
            .includes(q),
      ),
    )
  }, [allInventories, search])

  // ---------------------------------------------------------------------------
  // Lookups: summary inventory (source / destination) by id
  // ---------------------------------------------------------------------------
  const summaryInvMap = useMemo(() => summaryToMap(summaryData), [summaryData])

  // ---------------------------------------------------------------------------
  // One entry per finished good of the whole lot (the export and the lot
  // numbers use all of them; the table shows the ones matching the search)
  // ---------------------------------------------------------------------------
  // Production schemas by `${itemId}__${label}` (loaded below); BACKWARD
  // source amounts follow them where the data cannot tell
  const requestedSchemas = useRef(new Set())
  const [schemas, setSchemas] = useState({})
  // "Perbarui data" also reloads the schemas (they can be edited meanwhile);
  // they are master data per item + process, so lot and direction keep them
  useEffect(() => {
    if (!refreshAt) return
    requestedSchemas.current = new Set()
    setSchemas({})
  }, [refreshAt])

  const allProducts = useMemo(
    () =>
      buildProducts(allInventories, {
        isBackward,
        summaryInvMap,
        sourceById,
        userId: ownerData?.userId,
        lotCode: row?.lotCode,
        schemas,
      }),
    [
      allInventories,
      isBackward,
      summaryInvMap,
      sourceById,
      ownerData?.userId,
      row?.lotCode,
      schemas,
    ],
  )
  const products = useMemo(() => {
    if (inventories === allInventories) return allProducts
    const ids = new Set(inventories.map((inv) => Number(inv?.id)))
    return allProducts.filter((p) => ids.has(Number(p.id)))
  }, [allProducts, inventories, allInventories])

  // Keep a valid selection among the listed (searched) inventories
  useEffect(() => {
    if (!products.length) return
    if (!products.some((p) => p.id === selectedId)) {
      setSelectedId(products[0].id)
    }
  }, [products, selectedId])

  const selected = products.find((p) => p.id === selectedId) || null

  // ---------------------------------------------------------------------------
  // Lot-level numbers (whole lot, whatever the search)
  // ---------------------------------------------------------------------------
  // Items of the whole lot: totalAmount per item and unit from
  // laporevents/inventories
  const lotItems = useMemo(() => {
    const items = new Map()
    ;(inventoriesData?.totalUnitItems || []).forEach((item) =>
      addToBreakdown(items, {
        itemId: item.itemId ?? item.inventoryItemId ?? item.id,
        name: item.inventoryNameItem || item.nameItem || '-',
        unit: item.unit || '',
        amount: Number(item.totalAmount) || 0,
      }),
    )
    return Array.from(items.values())
  }, [inventoriesData])

  const barangJadiTotals = useMemo(
    () =>
      lotItems
        .filter((x) => x.amount > 0)
        .map((x) => ({ ...x, total: x.amount })),
    [lotItems],
  )

  // BACKWARD: the source materials of every finished good ("Jejak bahan
  // sumber"), summed per item and unit for the card and the Excel summary
  const bahanUsage = useMemo(() => {
    const totals = new Map()
    allProducts.forEach((p) =>
      (p.lastStep?.materials || []).forEach((m) =>
        addToBreakdown(totals, {
          itemId: m.itemId,
          name: m.name,
          unit: m.unit,
          amount: m.amount,
        }),
      ),
    )
    return Array.from(totals.values())
  }, [allProducts])

  const jenisBahanTotals = useMemo(
    () =>
      bahanUsage
        .filter((x) => x.amount > 0)
        .map((x) => ({ ...x, total: x.amount })),
    [bahanUsage],
  )

  // FORWARD: who received goods of this lot (sent or sold), with the amount
  // per unit, from the same destinations as the detail panel; the same
  // receiver in two units gets two lines
  const penerimaTotals = useMemo(
    () => recipientUsage(allProducts).map((x) => ({ ...x, total: x.amount })),
    [allProducts],
  )
  const penerimaCount = new Set(penerimaTotals.map((x) => x.name)).size

  // ---------------------------------------------------------------------------
  // Production schema of each finished good (standard input per material)
  // ---------------------------------------------------------------------------
  const [fetchSchemas] = useLazyFetchMapSchemasQuery()
  const [fetchSchemaDetail] = useLazyGetSchemaDetailQuery()

  const loadSchema = useCallback(
    async (itemId, label) => {
      try {
        const list = await fetchSchemas({ itemId, size: 50 }).unwrap()
        const items = list?.items || []
        const match =
          items.find((x) => x.createMethodLabel === label) ||
          (items.length === 1 ? items[0] : null)
        if (!match) return { status: 'none' }
        const detail = await fetchSchemaDetail(match.id).unwrap()
        const output = (detail?.output || []).find(
          (o) => Number(o.itemId) === Number(itemId),
        )
        return {
          status: 'ok',
          // false when the only schema of the item was taken as a fallback
          exact: match.createMethodLabel === label,
          label: detail?.createMethodLabel || match.createMethodLabel,
          outputAmount: Number(output?.amount) || 1,
          outputUnit: output?.expectedUnit || null,
          inputs: (detail?.input || []).map((i) => ({
            itemId: i.itemId,
            name: i.nameItem || '-',
            reqAmount: Number(i.reqAmount) || 0,
            unit: i.expectedUnit || i.unit || null,
          })),
          outputs: (detail?.output || []).map((o) => ({
            itemId: o.itemId,
            name: o.nameItem || '-',
            amount: Number(o.amount) || 0,
            unit: o.expectedUnit || o.unit || null,
          })),
        }
      } catch {
        return { status: 'none' }
      }
    },
    [fetchSchemas, fetchSchemaDetail],
  )

  // Load the schema of every listed finished good (once per item + label)
  useEffect(() => {
    products.forEach((p) => {
      if (!p.itemId) return
      const key = schemaKey(p)
      if (requestedSchemas.current.has(key)) return
      requestedSchemas.current.add(key)
      loadSchema(p.itemId, p.schemaLabel).then((result) =>
        setSchemas((prev) => ({ ...prev, [key]: result })),
      )
    })
  }, [products, loadSchema])

  // Schemas of every process step of the selected finished good
  const requestSchema = useCallback(
    (itemId, label) => {
      const key = `${itemId}__${label}`
      if (requestedSchemas.current.has(key)) return
      requestedSchemas.current.add(key)
      loadSchema(itemId, label).then((result) =>
        setSchemas((prev) => ({ ...prev, [key]: result })),
      )
    },
    [loadSchema],
  )

  useEffect(() => {
    if (!isBackward || !selected) return
    selected.steps.forEach((step) =>
      step.processes
        .filter((pr) => !SYSTEM_METHODS.includes(pr.label))
        .forEach((pr) => requestSchema(pr.itemId, pr.label)),
    )
  }, [isBackward, selected, requestSchema])

  // Every process behind the source amounts of the whole lot, so the page
  // and the Excel export use the same schemas
  useEffect(() => {
    if (!isBackward) return
    allProducts.forEach((p) =>
      p.sourceSchemas.forEach(({ itemId, label }) =>
        requestSchema(itemId, label),
      ),
    )
  }, [isBackward, allProducts, requestSchema])

  const getSchema = (p) =>
    p?.itemId
      ? schemas[schemaKey(p)] || { status: 'loading' }
      : { status: 'none' }

  const schema = getSchema(selected)

  // ---------------------------------------------------------------------------
  // Excel export (same input as before)
  // ---------------------------------------------------------------------------
  // The export always covers the whole lot, whatever the search
  const resolveSummary = useCallback(
    (sid) => {
      const summary = summaryInvMap.get(Number(sid))
      if (summary) return summary
      const fallback = allInventories.find(
        (inv) => Number(inv?.id) === Number(sid),
      )
      if (!fallback) return null
      return {
        id: fallback.id,
        amount: fallback.amount,
        initialAmount: fallback.initialAmount,
        unit: fallback.unit,
        ownerUserId: fallback.ownerUserId,
        item: fallback.item,
        warehouse: fallback.warehouse || null,
        owner: fallback.owner || null,
        ownerpartnership: fallback.ownerpartnership || null,
        extraData: fallback.extraData || null,
      }
    },
    [summaryInvMap, allInventories],
  )

  const displayedRows = useMemo(() => {
    const blankRow = { isBlank: true, item: { nameItem: '-' }, unit: '-' }
    const rows = []
    allInventories.forEach((inv) => {
      // BACKWARD: first-place sources, with the stock of the first source
      const methodOf = ({ summary, method }) =>
        method ||
        (inv?.summaryInvData || []).find(
          (d) => Number(d.id) === Number(summary?.id),
        )?.createMethodLabel ||
        ''
      const compare = (a, b) =>
        String(a).localeCompare(String(b), 'id', {
          numeric: true,
          sensitivity: 'base',
        })
      // Excel rows of a group: by Metode, then Jenis Barang (A-Z); rows
      // without a method go last
      const summaries = (
        isBackward
          ? exportSources(inv, resolveSummary, schemas)
          : (inv?.summaryInvIds || [])
              .map(resolveSummary)
              .filter(Boolean)
              .map((summary) => ({ summary }))
      ).sort(
        (a, b) =>
          !methodOf(a) - !methodOf(b) ||
          compare(methodOf(a), methodOf(b)) ||
          compare(
            a.summary?.item?.nameItem ?? '',
            b.summary?.item?.nameItem ?? '',
          ),
      )
      if (summaries.length > 0) {
        summaries.forEach(
          ({ summary, firstAmount, usedAmount, method, estimated }, idx) => {
            rows.push({
              inventory: idx === 0 ? inv : null,
              summary,
              firstAmount,
              usedAmount,
              method,
              estimated,
              isFirstRow: idx === 0,
              _inventoryId: inv?.id,
              _rowSpan: idx === 0 ? summaries.length : 0,
            })
          },
        )
      } else {
        rows.push({
          inventory: inv,
          summary: blankRow,
          isFirstRow: true,
          _inventoryId: inv?.id,
          _rowSpan: 1,
        })
      }
    })
    return rows
  }, [allInventories, resolveSummary, isBackward, schemas])

  const exportPayload = useMemo(() => {
    const bahanItems = isBackward ? bahanUsage : lotItems
    // FORWARD: where the lot went, same destinations as the page
    const barangJadiItems = isBackward
      ? lotItems
      : destinationUsage(allProducts)
    // One balance per unit: Set and Kg are never added together
    const balances = conversionBalance(bahanItems, barangJadiItems).map(
      (b) => ({
        ...b,
        unit: capitalizeEveryWord(b.unit || ''),
        balanceHint:
          b.selisihPct == null ? '' : `${b.selisihPct}% dari bahan masuk`,
      }),
    )
    const bahanTitle = isBackward ? 'Asal Barang' : 'Lot Ini'
    const jadiTitle = isBackward ? 'Lot Ini' : 'Tujuan Barang'

    return {
      summary: {
        balances,
        sentence: balances
          .map(
            (b) =>
              `Bahan masuk ${fmtQty(b.bahanMasuk, b.unit)}, barang jadi ${fmtQty(b.barangJadi, b.unit)}.`,
          )
          .join(' '),
        readingGuide: `Bahan Masuk = ${bahanTitle}. Barang Jadi = ${jadiTitle}.`,
      },
      bahanItems,
      barangJadiItems,
    }
  }, [lotItems, allProducts, isBackward, bahanUsage])

  const onDownload = () =>
    handleDownloadExcel(
      {
        displayedRows,
        ownerUserId: ownerData?.userId,
        direction: dir,
        lotCode: row?.lotCode ?? '-',
        dateLabel: formatDatePMBV2(row?.createdAt) || '-',
        typeLabel: TYPE_LABEL_MAP[row?.eventName] || '-',
        ...exportPayload,
      },
      setIsDownload,
      setFirstRows,
      setTotalRows,
      escrowpaymentId,
    )

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  const lotLabel = row?.lotCode ?? id

  // "Kode Inventori X · Lot ID Y", leaving out a missing inventory code
  const codeAndLot = (x) =>
    [x.code && `Kode Inventori ${x.code}`, `Lot ID ${x.lot || '-'}`]
      .filter(Boolean)
      .join(' · ')
  const muted = { color: colors.neutral[500] }

  const renderDirectionSwitch = () => (
    <div>
      <div
        role="group"
        aria-label="Pilih arah penelusuran"
        className="d-inline-flex p-1 rounded-3"
        style={{ backgroundColor: colors.neutral[200], gap: 4 }}
      >
        {[
          {
            key: 'BACKWARD',
            label: 'Asal Barang',
            color: colors.primary[500],
            iconStart: <ArrowLeft size={16} />,
          },
          {
            key: 'FORWARD',
            label: 'Tujuan Barang',
            color: colors.primary[500],
            iconEnd: <ArrowRight size={16} />,
          },
        ].map((tab) => {
          const active = dir === tab.key
          return (
            <button
              key={tab.key}
              type="button"
              aria-pressed={active}
              data-testid={`mutation-direction-${tab.key}`}
              onClick={() => handleChangeDirection(tab.key)}
              className="d-flex align-items-center gap-2 border-0 fw-bold"
              style={{
                minHeight: 44,
                padding: '0 20px',
                borderRadius: 9,
                fontSize: 15,
                backgroundColor: active ? tab.color : 'transparent',
                color: active ? colors.white : colors.neutral[700],
              }}
            >
              {tab.iconStart}
              {tab.label}
              {tab.iconEnd}
            </button>
          )
        })}
      </div>
      <p
        className="mb-0 mt-2"
        style={{ fontSize: 14, color: colors.neutral[600] }}
      >
        {isBackward
          ? 'Dari mana bahan setiap barang jadi berasal.'
          : 'Ke mana setiap barang dikirim atau digunakan.'}
      </p>
    </div>
  )

  // KPI card: big number, with an optional collapsible per-item breakdown
  // ({ itemId, name, unit, total }[])
  const renderKpi = (label, value, details, loading = false) => {
    const open = kpiDetailsOpen && !loading
    return (
      <div className="p-3 h-100" style={cardStyle}>
        <div style={{ fontSize: 14, color: colors.neutral[600] }}>{label}</div>
        <div
          className="fw-bolder"
          style={{ fontSize: 26, color: colors.neutral[900] }}
        >
          {loading ? (
            // Same footprint as the number and the "Lihat rincian" link
            <div aria-busy="true" aria-label={`Memuat ${label}`}>
              <Skeleton width={56} height={32} style={{ margin: '4px 0' }} />
              <Skeleton width={96} height={13} style={{ marginTop: 8 }} />
            </div>
          ) : (
            value
          )}
        </div>
        {!loading && details?.length > 0 && (
          <>
            <button
              type="button"
              className="btn btn-link p-0 mt-1 d-flex align-items-center gap-1 text-decoration-none"
              style={{ fontSize: 13, color: colors.neutral[600] }}
              onClick={() => setKpiDetailsOpen((prev) => !prev)}
            >
              {open ? 'Sembunyikan rincian' : 'Lihat rincian'}
              <ChevronDown
                size={14}
                style={{
                  transition: 'transform 0.25s ease',
                  transform: open ? 'rotate(180deg)' : 'none',
                }}
              />
            </button>
            <Collapse isOpen={open}>
              <div
                className="d-flex flex-column gap-1 mt-2 pt-2"
                style={{
                  fontSize: 14,
                  borderTop: `1px solid ${colors.neutral[200]}`,
                }}
              >
                {/* Every card lists its lines by name A-Z */}
                {[...details]
                  .sort(
                    (a, b) =>
                      String(a.name ?? '').localeCompare(
                        String(b.name ?? ''),
                        'id',
                        { numeric: true, sensitivity: 'base' },
                      ) ||
                      String(a.unit ?? '').localeCompare(String(b.unit ?? '')),
                  )
                  .map((t) => (
                    <div
                      key={`${t.itemId ?? t.name}__${t.unit}`}
                      className="d-flex justify-content-between gap-2"
                    >
                      <span>{t.name}</span>
                      <span className="fw-bold text-nowrap">
                        {fmtQty(t.total, t.unit)}
                      </span>
                    </div>
                  ))}
              </div>
            </Collapse>
          </>
        )}
      </div>
    )
  }

  const tableColumns = isBackward
    ? 'minmax(0,2.4fr) minmax(0,1.2fr) minmax(0,1fr) 20px'
    : 'minmax(0,2.4fr) minmax(0,1.2fr) minmax(0,1fr) minmax(0,1.2fr) 20px'

  // Loading placeholders: the same Skeleton as the KPI cards, laid out like
  // the rows / detail they stand in for
  const renderTableSkeleton = (rows = 6) => (
    <div aria-busy="true" aria-label={`Memuat ${itemLabel}`}>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: tableColumns,
            gap: 12,
            alignItems: 'center',
            padding: '12px 16px',
            minHeight: 56,
            borderBottom: `1px solid ${colors.neutral[100]}`,
          }}
        >
          <span className="d-flex flex-column gap-1">
            <Skeleton width="70%" height={15} />
            <Skeleton width="45%" height={12} />
          </span>
          <Skeleton width="80%" height={15} style={{ marginLeft: 'auto' }} />
          <Skeleton width="40%" height={14} style={{ marginLeft: 'auto' }} />
          {!isBackward && (
            <Skeleton width="80%" height={14} style={{ marginLeft: 'auto' }} />
          )}
          <span />
        </div>
      ))}
    </div>
  )

  const renderDetailSkeleton = () => (
    <div
      className="d-flex flex-column gap-3 p-3"
      aria-busy="true"
      aria-label="Memuat detail"
    >
      <Skeleton width="55%" height={20} />
      <Skeleton width="35%" height={13} />
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} height={56} borderRadius="10px" />
      ))}
    </div>
  )

  const renderTableRow = (p) => {
    const active = p.id === selectedId
    return (
      <button
        key={p.id}
        ref={(el) => {
          rowRefs.current[p.id] = el
        }}
        type="button"
        aria-pressed={active}
        onClick={() => handleSelectRow(p.id)}
        className="w-100 border-0 text-start"
        style={{
          display: 'grid',
          gridTemplateColumns: tableColumns,
          gap: 12,
          alignItems: 'center',
          padding: '12px 16px',
          minHeight: 56,
          borderBottom: `1px solid ${colors.neutral[100]}`,
          backgroundColor: active ? colors.primary[25] : colors.white,
          color: colors.neutral[900],
        }}
      >
        <span className="d-flex flex-column" style={{ minWidth: 0 }}>
          <span className="fw-semibold text-truncate" style={{ fontSize: 15 }}>
            {p.name}
          </span>
          <span style={{ ...muted, fontSize: 12 }}>{codeAndLot(p)}</span>
        </span>
        <span className="text-end fw-bold text-nowrap" style={{ fontSize: 15 }}>
          {fmtQty(p.qty, p.unit)}
        </span>
        <span className="text-end" style={{ fontSize: 14 }}>
          {isBackward ? p.jenisBahan : p.destCount}
        </span>
        {!isBackward && (
          <span className="text-end text-nowrap" style={{ fontSize: 14 }}>
            {fmtQty(p.remaining, p.unit)}
          </span>
        )}
        <ChevronRight size={18} style={{ color: colors.neutral[400] }} />
      </button>
    )
  }

  const renderLots = (lots) =>
    lots.length
      ? `${lots.slice(0, LOT_PREVIEW_COUNT).join(', ')}${
          lots.length > LOT_PREVIEW_COUNT
            ? ` +${lots.length - LOT_PREVIEW_COUNT} lainnya`
            : ''
        }`
      : '-'

  // Standard input of a material for the step it went into: the step's
  // schema (reqAmount / output amount) times what that step produced
  const renderMaterial = (step) => (b) => {
    // Source materials carry their own schema amount for this item
    const std =
      b.standard !== undefined ? b.standard : stepStandard(schemas, step, b)
    const usage = std ? Math.round((b.amount / std) * 100) : null
    const tone = schemaTone(usage)
    const withSchema = stepHasSchema(schemas, step)
    return (
      <div
        key={b.key}
        className="d-flex justify-content-between align-items-center gap-3 p-2"
        style={{
          borderRadius: 10,
          backgroundColor: colors.white,
          border: withSchema
            ? `2px solid ${colors.primary[300]}`
            : `1px solid ${colors.neutral[200]}`,
        }}
      >
        <span className="d-flex flex-column" style={{ minWidth: 0 }}>
          <span className="fw-semibold" style={{ fontSize: 15 }}>
            {b.name}
          </span>
          <span style={{ fontSize: 12, color: colors.neutral[500] }}>
            Lot ID: {renderLots(b.lots)}
            {b.ids.length > 1 ? ` · ${b.ids.length} inventori` : ''}
          </span>
          {/* Stock of the first source, before any split */}
          {b.firstStock > 0 && (
            <span
              className="fw-bold"
              style={{ fontSize: 12, color: colors.neutral[800] }}
            >
              {b.firstStock - b.amount > 0.0001
                ? `Dipakai ${fmtQty(b.amount, b.unit)} dari stok ${fmtQty(b.firstStock, b.unit)}`
                : `Dipakai seluruh stok ${fmtQty(b.firstStock, b.unit)}`}
            </span>
          )}
          {/* No schema to split a shared parent: not an exact number */}
          {b.estimated && (
            <span style={{ fontSize: 12, color: colors.neutral[600] }}>
              Jumlah perkiraan, skema tidak ditemukan
            </span>
          )}
          {std != null && (
            <span style={{ fontSize: 12, color: colors.neutral[500] }}>
              Standar skema {fmtQty(Math.round(std * 100) / 100, b.unit)}
            </span>
          )}
        </span>
        <span className="d-flex flex-column align-items-end gap-1">
          <span className="fw-bolder text-nowrap" style={{ fontSize: 15 }}>
            {fmtQty(b.amount, b.unit)}
          </span>
          {std != null && (
            <span
              className="text-nowrap"
              style={{
                padding: '1px 8px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                backgroundColor: tone.bg,
                color: tone.fg,
                border: `1px solid ${tone.fg}20`,
              }}
            >
              {usage}% dari standar
            </span>
          )}
        </span>
      </div>
    )
  }

  // One step from the first-place sources down to the finished good;
  // intermediate products and splits are left out
  const renderBahanList = () => {
    const steps = selected.lastStep ? [selected.lastStep] : []
    if (!steps.length) {
      return (
        <span style={{ ...muted, fontSize: 14 }}>
          Tidak ada data bahan sumber.
        </span>
      )
    }
    const connector = (step) => (
      <div
        className="d-flex gap-2"
        style={{
          padding: '6px 0 6px 12px',
          color: stepHasSchema(schemas, step)
            ? colors.primary[500]
            : colors.neutral[500],
        }}
      >
        <ArrowDown size={16} className="flex-shrink-0 mt-1" />
        <span style={{ fontSize: 12, fontWeight: 600 }}>
          {step.methods.join(' → ') || 'Diproses'}
        </span>
      </div>
    )

    return (
      <div className="d-flex flex-column">
        {steps.map((step) => (
          <div key={step.depth} className="d-flex flex-column">
            <div className="d-flex flex-column gap-2">
              {/* by item name A-Z, then unit */}
              {[...step.materials]
                .sort(
                  (a, b) =>
                    String(a.name ?? '').localeCompare(
                      String(b.name ?? ''),
                      'id',
                      { numeric: true, sensitivity: 'base' },
                    ) ||
                    String(a.unit ?? '').localeCompare(String(b.unit ?? '')),
                )
                .map(renderMaterial(step))}
            </div>
            {connector(step)}
          </div>
        ))}
        <div
          className="d-flex justify-content-between align-items-center gap-3 p-2"
          style={{
            borderRadius: 10,
            backgroundColor: colors.primary[25],
            border: `1px solid ${colors.primary[50]}`,
          }}
        >
          <span className="d-flex flex-column" style={{ minWidth: 0 }}>
            <span style={{ fontSize: 12, color: colors.primary[600] }}>
              Barang jadi
            </span>
            {/* Opens the product's trace page (Cerita Produk) in a new tab */}
            {selected.traceCode ? (
              <a
                href={`${process.env.REACT_APP_INVENTORY_URL}?productId=${encodeURIComponent(selected.traceCode)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="fw-bold d-inline-flex align-items-center gap-1 align-self-start"
                style={{ fontSize: 15, color: colors.primary[500] }}
              >
                {selected.name}
                <ExternalLink size={14} className="flex-shrink-0" />
              </a>
            ) : (
              <span className="fw-bold" style={{ fontSize: 15 }}>
                {selected.name}
              </span>
            )}
          </span>
          <span className="fw-bolder text-nowrap" style={{ fontSize: 15 }}>
            {fmtQty(selected.qty, selected.unit)}
          </span>
        </div>
      </div>
    )
  }

  // FORWARD: every process after this inventory as a tree, one card per
  // inventory, down to where each one ended up
  const countNodes = (nodes) =>
    nodes.reduce((t, n) => t + 1 + countNodes(n.children), 0)

  // Inventories per status over the whole tree
  const countByStatus = (nodes, counts = new Map()) => {
    nodes.forEach((n) => {
      counts.set(n.statusKey, (counts.get(n.statusKey) || 0) + 1)
      countByStatus(n.children, counts)
    })
    return counts
  }

  // Keep the inventories with this status and the path leading to them;
  // the path itself (another status) is shown faded
  const filterTree = (nodes, status) =>
    nodes.flatMap((n) => {
      const children = filterTree(n.children, status)
      const match = n.statusKey === status
      if (!match && !children.length) return []
      return [{ ...n, children, dimmed: !match }]
    })

  // A filtered-out step on the way is faded on its own; opacity on the
  // wrapper would also fade (and stack on) the matches nested below it
  const renderFlowNode = (n) => (
    <div key={n.id} className="d-flex flex-column">
      <div
        className="d-flex gap-2"
        style={{
          padding: '6px 0 6px 12px',
          color: colors.neutral[500],
          opacity: n.dimmed ? 0.5 : 1,
        }}
      >
        <ArrowDown size={16} className="flex-shrink-0 mt-1" />
        <span style={{ fontSize: 12, fontWeight: 600 }}>
          {n.method || 'Diproses'}
        </span>
      </div>
      <div
        className="d-flex justify-content-between align-items-center gap-3 p-2"
        style={{
          borderRadius: 10,
          backgroundColor: colors.white,
          border: `1px solid ${colors.neutral[200]}`,
          borderLeft: `4px solid ${
            STATUS_META[n.statusKey]?.color || colors.neutral[300]
          }`,
          opacity: n.dimmed ? 0.5 : 1,
        }}
      >
        <span className="d-flex flex-column" style={{ minWidth: 0 }}>
          <span className="fw-semibold" style={{ fontSize: 15 }}>
            {n.name}
          </span>
          <span style={{ fontSize: 12, color: colors.neutral[500] }}>
            Lot ID: {n.lot}
            {n.code ? ` · ${n.code}` : ''}
          </span>
          {/* Status color, as in the "Arti Warna" legend */}
          <span
            className="d-flex align-items-center gap-1"
            style={{ fontSize: 12, color: colors.neutral[700] }}
          >
            <span
              className="flex-shrink-0"
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor:
                  STATUS_META[n.statusKey]?.color || colors.neutral[300],
              }}
            />
            {n.status}
            {n.destination && n.destination !== '-'
              ? ` · ${n.destination}`
              : ''}
          </span>
        </span>
        <span className="d-flex flex-column align-items-end">
          <span className="fw-bolder text-nowrap" style={{ fontSize: 15 }}>
            {fmtQty(n.qty, n.unit)}
          </span>
          {/* Production: what this step made, in its own unit */}
          {n.output != null && (
            <span
              className="text-nowrap"
              style={{ fontSize: 12, color: colors.neutral[600] }}
            >
              Hasil {fmtQty(n.output, n.stockUnit)}
            </span>
          )}
          {/* Part of it went on to the next step: what is still here */}
          {n.stockNow != null &&
            (n.output != null
              ? Math.abs(n.stockNow - n.output) > 0.0001
              : Math.abs(n.stockNow - n.qty) > 0.0001) && (
              <span
                className="text-nowrap"
                style={{ fontSize: 12, color: colors.neutral[600] }}
              >
                Sisa {fmtQty(n.stockNow, n.stockUnit)}
              </span>
            )}
        </span>
      </div>
      {n.children.length > 0 && (
        <div
          className="d-flex flex-column"
          style={{
            marginLeft: 12,
            paddingLeft: 12,
            borderLeft: `2px solid ${colors.neutral[200]}`,
          }}
        >
          {n.children.map(renderFlowNode)}
        </div>
      )}
    </div>
  )

  const renderProcessFlow = () => {
    const tree = selected.forwardTree || []
    if (!tree.length) return null
    const counts = countByStatus(tree)
    const statuses = STATUS_ORDER.filter((st) => counts.has(st))
    const shown = flowStatus ? filterTree(tree, flowStatus) : tree
    const chip = (key, label, count, color) => {
      const active = flowStatus === key
      return (
        <button
          key={key || 'all'}
          type="button"
          aria-pressed={active}
          onClick={() => setFlowStatus(key)}
          className="d-flex align-items-center gap-2 fw-semibold"
          style={{
            minHeight: 36,
            padding: '0 12px',
            borderRadius: 999,
            fontSize: 13,
            border: `1px solid ${active ? colors.primary[500] : colors.neutral[300]}`,
            backgroundColor: active ? colors.primary[25] : colors.white,
            color: active ? colors.primary[600] : colors.neutral[700],
          }}
        >
          {color && (
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: color,
              }}
            />
          )}
          {label}
          <span style={{ color: colors.neutral[500] }}>
            {newFormatThousandSeparator(count)}
          </span>
        </button>
      )
    }
    return (
      <div className="d-flex flex-column gap-2">
        {/* also while a filter is on, so it can always be cleared */}
        {(statuses.length > 1 || flowStatus) && (
          <div
            className="d-flex flex-wrap gap-2"
            role="group"
            aria-label="Filter status alur proses"
          >
            {chip('', 'Semua', countNodes(tree))}
            {statuses.map((st) =>
              chip(
                st,
                STATUS_META[st].label,
                counts.get(st),
                STATUS_META[st].color,
              ),
            )}
          </div>
        )}
        <div className="d-flex flex-column">
          <div
            className="d-flex justify-content-between align-items-center gap-3 p-2"
            style={{
              borderRadius: 10,
              backgroundColor: colors.primary[25],
              border: `1px solid ${colors.primary[50]}`,
            }}
          >
            <span className="d-flex flex-column" style={{ minWidth: 0 }}>
              <span style={{ fontSize: 12, color: colors.primary[600] }}>
                Lot ini · {newFormatThousandSeparator(countNodes(tree))}{' '}
                inventori turunan
              </span>
              <span className="fw-bold" style={{ fontSize: 15 }}>
                {selected.name}
              </span>
            </span>
            <span className="fw-bolder text-nowrap" style={{ fontSize: 15 }}>
              {fmtQty(selected.qty, selected.unit)}
            </span>
          </div>
          {shown.length ? (
            shown.map(renderFlowNode)
          ) : (
            <span className="pt-3" style={{ ...muted, fontSize: 14 }}>
              Tidak ada inventori dengan status ini.
            </span>
          )}
        </div>
      </div>
    )
  }

  // Colour legend, same layout as the ESG dashboard ("Arti Warna")
  const swatch = (bg, round = false) => (
    <span
      className="d-inline-block me-2 align-middle flex-shrink-0"
      style={{
        width: 20,
        height: 20,
        backgroundColor: bg,
        border: '1px solid #d1d5db',
        borderRadius: round ? '50%' : 4,
      }}
    />
  )

  const renderLegend = () => {
    const rows = isBackward
      ? SCHEMA_LEGEND.map((l) => ({
          key: String(l.pct),
          swatch: swatch(schemaTone(l.pct).bg),
          text: l.text,
        }))
      : STATUS_ORDER.map((status) => ({
          key: status,
          swatch: swatch(STATUS_META[status].color, true),
          text: STATUS_META[status].label,
        }))
    return (
      <div
        className="mt-4 pt-3"
        style={{ borderTop: `2px solid ${colors.neutral[200]}` }}
      >
        <h5 className="mb-3 fw-bold">Arti Warna</h5>
        <div
          className="overflow-hidden"
          style={{
            backgroundColor: colors.white,
            borderRadius: 8,
            border: '1px solid #dee2e6',
            maxWidth: 640,
          }}
        >
          <div className="fw-semibold p-3 bg-light border-bottom">
            {isBackward ? 'Asal Barang' : 'Tujuan Barang'}
          </div>
          <table className="w-100 small">
            <thead>
              <tr>
                <th className="p-3 text-start">Penilaian</th>
                <th className="p-3 text-start">Arti Warna</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, index) => (
                <tr key={r.key}>
                  {index === 0 && (
                    <td
                      rowSpan={rows.length}
                      className="p-3 align-middle border-top"
                      style={{ width: '40%' }}
                    >
                      {isBackward
                        ? 'Susut dan pemakaian bahan dibanding standar skema'
                        : 'Status tujuan barang'}
                    </td>
                  )}
                  <td className="p-3 border-top">
                    <span className="d-flex align-items-center">
                      {r.swatch}
                      {r.text}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // How the loss percentage of the selected finished good is calculated,
  // and how it compares with the loss expected by the schema
  const renderLoss = () => {
    const {
      inputs: bahan,
      bahanTotal,
      processQty: qty,
      processLabel,
      producedBySplit,
      unit,
      lossPct,
    } = selected
    const note = (text) => (
      <span style={{ fontSize: 13, color: colors.neutral[600] }}>{text}</span>
    )

    let body
    if (!bahan.length) {
      body = note(
        'Tidak ditemukan proses yang mengubah bahan menjadi barang ini, jadi susut belum bisa dihitung.',
      )
    } else if (lossPct == null) {
      const bahanUnits = [
        ...new Set(bahan.map((b) => capitalizeEveryWord(b.unit || '-'))),
      ].join(', ')
      body = note(
        `Susut tidak dihitung karena satuan bahan (${bahanUnits}) berbeda dengan satuan barang jadi (${capitalizeEveryWord(unit || '-')}).`,
      )
    } else {
      const { lossQty, expectedLossPct, usage, verdict } = processComparison(
        selected,
        schema,
      )
      const tone = schemaTone(usage)

      body = (
        <>
          {[
            [
              'Bahan masuk proses',
              bahan
                .map((b) => `${b.name} ${fmtQty(b.amount, b.unit)}`)
                .join(', '),
            ],
            ['Hasil proses', `${selected.name} ${fmtQty(qty, unit)}`],
            ['Susut', `${fmtQty(lossQty, unit)} (${lossPct}%)`],
          ].map(([label, value], i) => (
            <div
              key={label}
              className="d-flex justify-content-between gap-3"
              style={{
                fontSize: 14,
                paddingTop: i === 2 ? 8 : 0,
                borderTop: i === 2 ? `1px solid ${colors.neutral[200]}` : 0,
              }}
            >
              <span style={{ color: colors.neutral[600] }}>{label}</span>
              <span className={i === 2 ? 'fw-bolder' : 'fw-semibold'}>
                {value}
              </span>
            </div>
          ))}
          <span style={{ ...muted, fontSize: 12 }}>
            ({fmtQty(bahanTotal, unit)} − {fmtQty(qty, unit)}) ÷{' '}
            {fmtQty(bahanTotal, unit)} × 100 = {lossPct}%
          </span>
          {lossPct < 0 &&
            note(
              'Barang jadi tercatat lebih banyak dari bahannya. Periksa kembali pencatatan.',
            )}
          {schema.status === 'loading' && note('Memuat perbandingan skema...')}
          {schema.status !== 'loading' &&
            usage == null &&
            note('Tidak ada standar skema untuk dibandingkan.')}
          {usage != null && (
            <div
              className="d-flex flex-column gap-1 p-2"
              style={{
                borderRadius: 10,
                backgroundColor: tone.bg,
                color: tone.fg,
                border: `1px solid ${tone.fg}20`,
              }}
            >
              <span className="fw-bold" style={{ fontSize: 14 }}>
                {verdict}
              </span>
              <span style={{ fontSize: 13 }}>
                Perkiraan susut menurut skema {expectedLossPct}%, susut aktual{' '}
                {lossPct}%. Pemakaian bahan {usage}% dari standar skema.
              </span>
            </div>
          )}
        </>
      )
    }

    return (
      <section
        aria-label="Susut"
        className="d-flex flex-column gap-2 p-3"
        style={{
          border: `1px solid ${colors.neutral[200]}`,
          borderRadius: 12,
          backgroundColor: colors.neutral[50],
        }}
      >
        <div className="d-flex flex-column">
          <span className="fw-bold" style={{ fontSize: 15 }}>
            Susut
          </span>
          <span style={{ ...muted, fontSize: 12 }}>
            Bahan yang hilang saat diproses
            {processLabel ? ` (${processLabel})` : ''}.
          </span>
        </div>
        {body}
        {producedBySplit && bahan.length > 0 && (
          <span style={{ fontSize: 12, color: colors.neutral[500] }}>
            Barang jadi ini ({fmtQty(selected.qty, selected.unit)}) adalah
            bagian dari hasil proses tersebut ({fmtQty(qty, unit)}), lalu
            dipecah atau dipindahkan.
          </span>
        )}
      </section>
    )
  }

  const renderBackToList = (bordered) =>
    !isDesktop && (
      <button
        type="button"
        onClick={backToList}
        className="w-100 d-flex align-items-center justify-content-center gap-2 border-0 bg-transparent fw-bold"
        style={{
          minHeight: 48,
          fontSize: 14,
          color: colors.primary[500],
          [bordered]: `1px solid ${colors.neutral[200]}`,
        }}
      >
        <ArrowUp size={16} />
        Kembali ke daftar {itemLabel}
      </button>
    )

  const renderDetail = () => {
    if (!selected) {
      return (
        <div className="p-4 text-center" style={{ ...muted, fontSize: 14 }}>
          Pilih satu {itemLabel} di tabel.
        </div>
      )
    }
    const line = isBackward
      ? `${
          selected.processLabel
            ? `Diproses lewat ${selected.processLabel}`
            : 'Tanpa proses'
        }${selected.lossPct != null ? ` · susut ${selected.lossPct}%` : ''}`
      : `Sisa di stok: ${fmtQty(selected.remaining, selected.unit)}`
    return (
      <>
        {renderBackToList('borderBottom')}
        <div
          className="d-flex flex-column gap-1"
          style={{
            padding: '18px 18px 14px',
            borderBottom: `1px solid ${colors.neutral[200]}`,
          }}
        >
          <span style={{ ...muted, fontSize: 13 }}>{codeAndLot(selected)}</span>
          <div className="d-flex justify-content-between align-items-baseline gap-3">
            <span className="fw-bolder" style={{ fontSize: 19 }}>
              {selected.name}
            </span>
            <span className="fw-bolder text-nowrap" style={{ fontSize: 22 }}>
              {fmtQty(selected.qty, selected.unit)}
            </span>
          </div>
          <span style={{ fontSize: 14, color: colors.neutral[700] }}>
            {line}
          </span>
        </div>
        <div
          className="d-flex flex-column gap-3"
          style={{ padding: '16px 18px' }}
        >
          {!isBackward && selected.forwardTree?.length > 0 && (
            <>
              <span className="fw-bold" style={{ fontSize: 15 }}>
                Alur proses
              </span>
              {renderProcessFlow()}
            </>
          )}
          {/* FORWARD: "Alur proses" above already shows where everything
              went; the per-status totals stay in the cards and the export */}
          {isBackward && (
            <>
              <span className="fw-bold" style={{ fontSize: 15 }}>
                Jejak bahan sumber
              </span>
              {renderBahanList()}
              {renderLoss()}
            </>
          )}
        </div>
        {renderBackToList('borderTop')}
      </>
    )
  }

  if (isPageLoading) {
    return (
      <div className="w-100 px-3 d-flex justify-content-center align-items-center py-5">
        <LoadingAnimation />
      </div>
    )
  }

  return (
    <div
      ref={pageRef}
      className="w-100 px-3 pb-4"
      style={{ color: colors.neutral[900] }}
    >
      <button
        type="button"
        aria-label="Kembali ke atas"
        title="Kembali ke atas"
        onClick={() =>
          (scrollerRef.current || window).scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        }
        className="d-flex align-items-center justify-content-center border-0"
        style={{
          position: 'fixed',
          right: 16,
          bottom: 24,
          zIndex: 1030,
          width: 44,
          height: 44,
          borderRadius: 999,
          backgroundColor: colors.primary[500],
          color: colors.white,
          boxShadow: `0 4px 12px ${colors.neutral[400]}66`,
          opacity: showBackToTop ? 1 : 0,
          transform: showBackToTop ? 'translateY(0)' : 'translateY(12px)',
          pointerEvents: showBackToTop ? 'auto' : 'none',
          transition: 'opacity 0.2s ease, transform 0.2s ease',
        }}
        tabIndex={showBackToTop ? 0 : -1}
      >
        <ArrowUp size={20} />
      </button>
      <PMBProgressBarExportFile
        isDialogShow={isDownload}
        firstRows={firstRows}
        totalRows={totalRows}
        onCancel={() => setIsDownload(false)}
      />
      <div className="pt-3">
        <BreadCrumbs
          breadCrumbActive={`Lot ${lotLabel}`}
          breadCrumbParent="Laporan Ketelusuran"
          breadCrumbParentLink={URL_MUTATION_REPORT}
        />
      </div>

      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
        <div>
          <h4 className="fw-bolder mb-1">Lot {lotLabel}</h4>
          <span style={{ fontSize: 14, color: colors.neutral[600] }}>
            {TYPE_LABEL_MAP[row?.eventName] || '-'} ·{' '}
            {formatDatePMBV2(row?.createdAt) || '-'}
          </span>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <CustomButton
            onClick={() => setRefreshAt(Date.now())}
            outline
            color="primary"
            // Each click is a new refreshAt, so RTK never dedupes: stay
            // disabled until every forced trace request has finished
            disabled={isBusy || isFetchingSourceSummary}
            testId="mutation-refresh-button"
            title="Perbarui data"
            aria-label="Perbarui data"
          >
            <RefreshCw size={15} />
          </CustomButton>
          <CustomButton
            onClick={onDownload}
            outline
            color="primary"
            disabled={isFetchingInventories}
          >
            <Download size={15} /> Unduh Excel
          </CustomButton>
        </div>
      </div>

      <div className="mb-3">{renderDirectionSwitch()}</div>

      {/* Simple lot numbers */}
      <Row className="g-3 mb-3">
        <Col xs="12" md="6">
          {renderKpi(
            `Jenis ${ItemLabel}`,
            newFormatThousandSeparator(barangJadiTotals.length),
            barangJadiTotals,
            isLotLoading,
          )}
        </Col>
        <Col xs="12" md="6">
          {isBackward
            ? renderKpi(
                'Jenis Bahan',
                newFormatThousandSeparator(jenisBahanTotals.length),
                jenisBahanTotals,
                isLotLoading || isSummaryLoading,
              )
            : renderKpi(
                'Penerima',
                newFormatThousandSeparator(penerimaCount),
                penerimaTotals,
                isLotLoading || isSummaryLoading,
              )}
        </Col>
      </Row>

      {/* List + detail */}
      <Row className="g-3 align-items-start">
        <Col xs="12" lg="6">
          <Card className="border-0 overflow-hidden" style={cardStyle}>
            <div
              className="d-flex flex-wrap gap-2 p-3"
              style={{ borderBottom: `1px solid ${colors.neutral[200]}` }}
            >
              <label
                className="d-flex align-items-center gap-2 flex-grow-1 mb-0 px-3"
                style={{
                  minHeight: 44,
                  minWidth: 200,
                  border: `1px solid ${colors.neutral[300]}`,
                  borderRadius: 10,
                  color: colors.neutral[500],
                }}
              >
                <Search size={18} />
                <input
                  type="search"
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value)
                    applySearch(e.target.value)
                  }}
                  placeholder="Cari nama barang atau kode inventori"
                  aria-label={`Cari ${itemLabel}`}
                  className="border-0 flex-grow-1 bg-transparent"
                  style={{ outline: 'none', fontSize: 14, minWidth: 0 }}
                />
              </label>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                aria-label="Urutkan"
                className="form-select w-auto"
                style={{ minHeight: 44, fontSize: 14, borderRadius: 10 }}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="table-responsive">
              <div style={{ minWidth: isBackward ? 420 : 520 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: tableColumns,
                    gap: 12,
                    padding: '10px 16px',
                    backgroundColor: colors.neutral[50],
                    borderBottom: `1px solid ${colors.neutral[200]}`,
                    fontSize: 12,
                    fontWeight: 700,
                    color: colors.neutral[500],
                  }}
                >
                  <span>{ItemLabel}</span>
                  <span className="text-end">Jumlah</span>
                  <span className="text-end">
                    {isBackward ? 'Jenis Bahan' : 'Tujuan'}
                  </span>
                  {!isBackward && <span className="text-end">Sisa</span>}
                  <span />
                </div>
                {isBusy ? (
                  renderTableSkeleton()
                ) : products.length === 0 ? (
                  <div
                    className="p-4 text-center"
                    style={{ ...muted, fontSize: 14 }}
                  >
                    {search
                      ? `Tidak ada ${itemLabel} yang cocok dengan pencarian.`
                      : `Tidak ada data ${itemLabel}.`}
                  </div>
                ) : (
                  products.map(renderTableRow)
                )}
              </div>
            </div>
            {!isBusy && allInventories.length > 0 && (
              <CardBody className="py-2" style={{ ...muted, fontSize: 13 }}>
                {search
                  ? `${newFormatThousandSeparator(products.length)} dari ${newFormatThousandSeparator(allInventories.length)} ${itemLabel}`
                  : `${newFormatThousandSeparator(allInventories.length)} ${itemLabel}`}
              </CardBody>
            )}
          </Card>
        </Col>
        {/* Stretch so the sticky detail can follow the list down the page */}
        <Col xs="12" lg="6" className="align-self-lg-stretch">
          <aside
            ref={detailRef}
            aria-label={`Detail ${itemLabel}`}
            className={isDesktop ? 'overflow-auto' : undefined}
            style={{
              ...cardStyle,
              scrollMarginTop: 16,
              ...(isDesktop && {
                position: 'sticky',
                top: 16,
                maxHeight: 'calc(100vh - 32px)',
              }),
            }}
          >
            {isBusy ? renderDetailSkeleton() : renderDetail()}
          </aside>
        </Col>
      </Row>

      {renderLegend()}
    </div>
  )
}
